import React, { useState } from 'react';
import { MdDashboard, MdDescription, MdLink, MdGroups, MdBusiness } from 'react-icons/md';
import AuthService from '../services/authService';
import axiosInstance from '../utils/axiosInstance';
import { getImageURL } from '../utils/api';
import { useHasMultipleWorkspaces } from '../utils/workspaces';
import { useTvecCourse } from '../utils/tvecCourse';
import ImpersonationBanner from './ImpersonationBanner';

const NAV = [
  { name: 'Dashboard', href: '/tvec/dashboard', icon: <MdDashboard /> },
  { name: 'Batches', href: '/tvec/batches', icon: <MdGroups /> },
  { name: 'Template', href: '/tvec/template', icon: <MdDescription /> },
  { name: 'Premises and Resources', href: '/tvec/premises', icon: <MdBusiness /> },
  { name: 'Links', href: '/tvec/links', icon: <MdLink /> },
];

function TvecSidebar({ isOpen }) {
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  return (
    <aside
      className="fixed inset-y-0 left-0 z-50 flex flex-col border-r border-gray-200 bg-white shadow-sm transition-all duration-300"
      style={{ width: isOpen ? 256 : 64 }}
    >
      <div className="flex h-16 items-center justify-center border-b border-gray-200 px-4">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-600 text-xs font-bold text-white">TV</div>
          {isOpen && <span className="text-base font-bold text-gray-900">TVEC</span>}
        </div>
      </div>
      <nav className="flex-1 space-y-1.5 overflow-y-auto px-3 py-4">
        {NAV.map((item) => {
          const active = currentPath.startsWith(item.href);
          return (
            <a
              key={item.name}
              href={item.href}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm no-underline ${active ? 'bg-sky-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
            >
              <span className="text-lg">{item.icon}</span>
              {isOpen && <span>{item.name}</span>}
            </a>
          );
        })}
      </nav>
      {isOpen && (
        <div className="border-t border-gray-200 p-4 text-xs text-gray-500">
          <div className="font-semibold">TVEC Panel</div>
        </div>
      )}
    </aside>
  );
}

function TvecTopBar({ title, onMenuClick }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [broken, setBroken] = useState(false);
  const multi = useHasMultipleWorkspaces();
  const { courseId, courses, setCourse } = useTvecCourse();
  const staff = AuthService.getUser()?.superAdmin?.staff || {};
  const name = staff.name || AuthService.getUser()?.superAdmin?.name || 'User';

  const handleLogout = async () => {
    try { await axiosInstance.post('/logout'); } catch {}
    AuthService.clear();
    window.location.href = '/';
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-gray-200 bg-white px-6 shadow-sm">
      <div className="flex items-center gap-4">
        <button type="button" onClick={onMenuClick} aria-label="Toggle menu" className="rounded-lg p-2 text-gray-600 hover:bg-gray-100">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" /></svg>
        </button>
        {title && <h1 className="hidden text-lg font-semibold text-gray-900 sm:block">{title}</h1>}
        <div className="flex items-center gap-2">
          <label htmlFor="tvec-course" className="hidden text-xs font-semibold uppercase tracking-wide text-gray-500 md:block">Course</label>
          <select
            id="tvec-course"
            value={courseId}
            onChange={(e) => setCourse(e.target.value)}
            aria-label="Select course"
            className="max-w-[11rem] rounded-lg border border-sky-200 bg-sky-50 py-2 pl-3 pr-8 text-sm font-semibold text-sky-800 shadow-sm outline-none transition hover:border-sky-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 sm:max-w-xs"
          >
            {courses.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="relative">
        <button type="button" onClick={() => setMenuOpen((v) => !v)} className="flex items-center gap-2 rounded-lg px-3 py-1.5 hover:bg-gray-100">
          {staff.imagePath && !broken ? (
            <img src={getImageURL(staff.imagePath)} alt={name} onError={() => setBroken(true)} className="h-8 w-8 rounded-full object-cover" />
          ) : (
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-sky-600 text-xs font-semibold text-white">
              {name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
            </span>
          )}
          <span className="text-sm font-medium text-gray-700">{name}</span>
        </button>
        {menuOpen && (
          <div className="absolute right-0 mt-2 w-56 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
            <div className="border-b border-gray-200 px-4 py-2.5">
              <p className="text-sm font-semibold text-gray-900">{name}</p>
              <p className="mt-0.5 text-xs text-gray-500">{staff.officeEmail || staff.gmail || ''}</p>
              <span className="mt-2 inline-block rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-800">TVEC</span>
            </div>
            {multi && (
              <button type="button" onClick={() => { window.location.href = '/workspaces'; }} className="block w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100">
                Switch workspace
              </button>
            )}
            <button type="button" onClick={handleLogout} className="block w-full border-t border-gray-200 px-4 py-2.5 text-left text-sm text-red-600 hover:bg-gray-100">
              Sign Out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

export default function TvecLayout({ children, title }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <TvecSidebar isOpen={sidebarOpen} />
      <div className="flex flex-1 flex-col overflow-hidden transition-all duration-300" style={{ marginLeft: sidebarOpen ? 256 : 80 }}>
        <ImpersonationBanner theme="emerald" />
        <TvecTopBar title={title} onMenuClick={() => setSidebarOpen((v) => !v)} />
        <main className="flex-1 overflow-y-auto overflow-x-hidden bg-gray-50 p-6">{children}</main>
      </div>
    </div>
  );
}
